import { decodeBody } from './decode';
import {
  ResponseDecodeError,
  ResponseTooLargeError,
  ResponseValidationError,
} from './errors';
import type { HttpErrorOptions } from './errors';
import { observedOf, readResponseBody, releaseBody } from './response-body';
import type {
  DecodedRequestResult,
  DecodedFailureResult,
  HttpResponse,
  RawHeadRequestResult,
  RawRequestResult,
  RequestOptionsWithoutContext,
  UntracedResult,
  ResponseBody,
  Validator,
} from './types';
import { validated } from './validate';

/* ---------------------------------------------------------------------------
 * The four steps between a transport's answer and the caller's result. Which
 * one runs is decided by the call's shape -- raw hands the stream over, decoded
 * reads it under the cap, and a schema checks the copy afterwards.
 *
 * They live here rather than in `request.ts` because they read the call from
 * their parameters and touch none of the state the pipeline keeps around them,
 * so moving them carried nothing along. `request.ts` sits against the line
 * ceiling its own ratchet sets, and this region could leave on those terms.
 * ------------------------------------------------------------------------- */

/**
 * The stream on a result that carries one, and `null` on every result that does
 * not.
 *
 * A decoded result has no `stream` at all and neither does a raw HEAD, which is
 * DECISION 3.6 doing its job: the absent property is what makes `'stream' in
 * result` narrow. A `204` carries the property with `null` in it.
 */
export function streamOf(
  result: UntracedResult<unknown> | undefined,
): ReadableStream<Uint8Array> | null {
  if (result === undefined || !('stream' in result)) return null;
  return result.stream;
}

/**
 * Lets go of a stream nobody will read, without making the caller wait for it.
 *
 * Not awaited on purpose: this runs while a call is already failing, and the
 * transport that produced the stream is the same one that ignored the deadline.
 * Waiting on it would hand that transport the delay the deadline just refused.
 *
 * WHEN this runs stays with the caller. Both lifecycle owners decide that for
 * themselves; this file only knows how to let go, never whether to.
 */
export function discardStream(
  result: UntracedResult<unknown> | undefined,
): void {
  const stream = streamOf(result);
  if (stream === null) return;
  void stream.cancel().catch(() => {});
}

/**
 * Hands the stream over exactly as it arrived: unread, uncounted, uncancelled.
 *
 * Reading one byte to inspect it would take that byte away from the caller, so
 * `raw` is the one path where the library looks at nothing.
 *
 * A HEAD answer never carries a body, so there is no stream to hand over and
 * the field is absent rather than permanently null — DECISION 3.6, the wrong
 * read is unspeakable instead of merely rejected.
 */
export function rawResultOf(
  method: string,
  response: HttpResponse,
): Omit<RawHeadRequestResult, 'trace'> | Omit<RawRequestResult, 'trace'> {
  const { headers, ok, status, statusText } = response;
  if (method === 'HEAD')
    return { body: undefined, headers, ok, status, statusText };
  return {
    body: undefined,
    headers,
    ok,
    status,
    statusText,
    stream: response.body,
  };
}

function decodedAnswer<T>(
  response: HttpResponse,
  body?: T,
):
  | Omit<DecodedRequestResult<T | undefined>, 'trace'>
  | Omit<DecodedFailureResult, 'trace'> {
  const { headers, status, statusText } = response;
  return response.ok
    ? { body, headers, ok: true, status, statusText }
    : { body, headers, ok: false, status, statusText };
}

/** A malformed non-ok response still reports its HTTP status. */
function undecodableFailure(
  response: HttpResponse,
  method: string,
  url: string,
  cause: unknown,
  failureOptions: () => HttpErrorOptions,
): Omit<DecodedFailureResult, 'trace'> {
  if (response.ok)
    throw new ResponseDecodeError(method, url, {
      ...failureOptions(),
      cause,
    });
  return {
    body: undefined,
    headers: response.headers,
    ok: false,
    status: response.status,
    statusText: response.statusText,
  };
}

/**
 * The body as the CALLER claimed it, with nothing checking that claim.
 *
 * We decode and hold a value of genuinely unknown shape, while the signature has
 * promised a `Report`. Nothing bridges that, because choosing
 * `request<Report>({ url })` IS choosing to skip the check. The assertion is not
 * the library guessing: it is the caller's claim, written down at the one point
 * where it enters the type system.
 *
 * MEASURED: TypeScript will not let an implementation producing `unknown`
 * satisfy a generic signature, so the alternative is to answer every call with
 * `unknown`. A `deserializer` produces `T` itself and `validate.response`
 * produces a checked one, both without an assertion; only this path arrives here.
 */
async function claimedBody<T>(
  body: ResponseBody,
  response: HttpResponse,
): Promise<T> {
  // Per line, not per file, so a second cast has to justify itself.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return (await decodeBody(body, response)) as T;
}

/**
 * Reads the answer under the cap and turns it into the caller's shape.
 *
 * The shape is decided by the answer's own content-type, or by a `deserializer`
 * that takes over entirely. There is no `responseType` option; this paragraph
 * named one until the string was dropped for the content-type route. Both ways
 * read through the same capped body, which is why streaming it yourself is not
 * an escape from `maxResponseBytes`.
 *
 * The parameter is a `Pick` of the two fields this reads rather than a whole
 * options variant, so a decoded call with a context and one without both land
 * here without a widening step in between.
 *
 * It does NOT check the shape. This runs INSIDE the shared region, and a schema
 * run here would be one caller's schema deciding for everyone who joined the
 * flight (VA9, CO16). `checkedResultOf` does that per caller, on the copy.
 *
 * `failureOptions` is the bag a failure from here carries: the caller's options
 * (ER10) and, outside the shared region, which call it was and how long it took
 * (ER63, ER66). It is separate from the `Pick` above, which is what this
 * function READS; this one it only hands on.
 *
 * A FUNCTION and not a value, because the duration is read when the failure is
 * built. The cap tripping is the case that makes the difference real: that read
 * can run for a long time after this was called, and a value would report the
 * moment the read began.
 *
 * What it produces may carry no options at all, because this runs inside the
 * shared region -- see `attributable` in `request` for who may claim those and
 * who may not.
 */
export async function decodedResultOf<T>(
  method: string,
  url: string,
  options: Pick<
    RequestOptionsWithoutContext<T>,
    'deserializer' | 'maxResponseBytes'
  >,
  response: HttpResponse,
  failureOptions: () => HttpErrorOptions,
): Promise<
  | Omit<DecodedRequestResult<T | undefined>, 'trace'>
  | Omit<DecodedFailureResult, 'trace'>
> {
  if (
    method === 'HEAD' ||
    response.status === 204 ||
    response.status === 205 ||
    response.status === 304
  ) {
    // HEAD, 204, 205 and 304 cannot carry response content. A custom transport
    // may still hand us a stream, so release it without waiting for that
    // transport to settle.
    if (response.body !== null) void response.body.cancel().catch(() => {});
    return decodedAnswer(response);
  }

  let cappedBody;
  try {
    cappedBody = await readResponseBody(
      response,
      options.maxResponseBytes,
      () => new ResponseTooLargeError(method, url, failureOptions()),
    );
  } catch (error) {
    if (error instanceof ResponseTooLargeError) throw error;
    return undecodableFailure(response, method, url, error, failureOptions);
  }

  if (cappedBody.empty) return decodedAnswer(response);

  let body: T;
  try {
    body =
      options.deserializer === undefined
        ? await claimedBody<T>(cappedBody.body, response)
        : await options.deserializer.deserialize(
            cappedBody.body,
            observedOf(response),
          );
  } catch (error) {
    // The cap tripping is the reason the read failed, and reporting it as a
    // decode failure would send the caller looking at the body instead of at
    // its size. Anything else is what it says it is: this body did not decode.
    const overflow = cappedBody.overflow();
    if (overflow !== undefined) throw overflow;
    return undecodableFailure(response, method, url, error, failureOptions);
  } finally {
    // Either way. A deserializer that declines to read leaves the connection
    // open just as surely as one that throws before reaching for it.
    await releaseBody(cappedBody.body);
  }

  // Reached with a value in hand, so the read did not reject — but a reader that
  // caught the cap failure itself would still be handing back half an answer.
  const overflow = cappedBody.overflow();
  if (overflow !== undefined) throw overflow;

  return decodedAnswer(response, body);
}

/**
 * This caller's own schema, on this caller's own copy of the answer.
 *
 * OUTSIDE the shared region, and that is what the flight makes necessary: run
 * inside it, whichever schema opened the flight would decide for everyone
 * behind it, and the second caller would receive a body checked against
 * somebody else's expectations (VA9, VA10, CO16).
 *
 * AFTER the copy, never before. The validated value REPLACES the body rather
 * than merely approving it (VA6) — a Zod `.transform()` is the everyday case —
 * so checking the shared value first would hand every joiner the first caller's
 * replaced one, which is the same leak one step earlier.
 *
 * Format first, shape second (VA11, VA12). The schema is handed the VALUE the
 * decode produced, never the bytes it produced it from: the other order would
 * make every schema written against the decoded shape reject.
 *
 * What `failureOptions` produces here always names a caller, unlike on
 * `decodedResultOf`, and that is the boundary saying so: this runs OUTSIDE the
 * shared region, so the failure it raises belongs to one caller and can carry
 * that caller's own options, id and duration (ER10, ER63, ER66).
 */
export async function checkedResultOf(
  method: string,
  url: string,
  schema: Validator<unknown> | undefined,
  result: UntracedResult<unknown>,
  failureOptions: () => HttpErrorOptions,
): Promise<UntracedResult<unknown>> {
  if (schema === undefined) return result;
  const validatedBody = await validated(
    schema,
    result.body,
    cause =>
      new ResponseValidationError(method, url, { ...failureOptions(), cause }),
  );
  const { headers, status, statusText } = result;
  return { body: validatedBody.value, headers, ok: true, status, statusText };
}
